import type React from "react";
import { useEffect } from "react";
import { trackEvent } from "@/lib/telemetry";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { BadgeCheck, Briefcase, ExternalLink, History, LayoutList, MessageCircle, Pencil } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { SidePanel } from "@/components/patterns";
import {
  AIBadge,
  ContactEmail,
  CopyButton,
  DateRange,
  EmptyValue,
  PersonAvatar,
  Pretensao,
  Rating,
  RelativeTime,
  TagList,
  formatPhoneBR,
} from "@/components/data";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { DisponibilidadeBadge } from "@/components/freelancers/DisponibilidadeBadge";
import { KanbanStageSelector } from "@/components/freelancers/KanbanStageSelector";
import { WhatsAppAguardandoBadge } from "@/components/whatsapp/WhatsAppBadges";
import { WhatsAppLinhaDoTempo } from "@/components/whatsapp/WhatsAppLinhaDoTempo";
import { LinkedInResumo } from "./LinkedInResumo";
import { CandidatoLinkedInTab } from "./CandidatoLinkedInTab";
import { CandidatoMensagensTab } from "./CandidatoMensagensTab";
import { LinkedInIcone, WhatsAppIcone } from "@/components/freelancers/contato/FreelancerContatoIcons";
import { formatLocation } from "@/components/freelancers/v2/freelancerCardShared";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import { useFreelancerPainel, useFreelancerResumoRapido } from "@/hooks/use-freelancer-resumo-rapido";
import { useFreelancerTimelineCompleta } from "@/hooks/useFreelancerTimelineCompleta";
import { supabase } from "@/integrations/supabase/client";
import { linkedInApi } from "@/services/linkedin-api";
import { whatsappApi } from "@/services/whatsapp-api";
import { toneTextClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import { OpenToWorkRing } from "@/components/freelancers/v2/OpenToWorkRing";
import type { FreelancerListItem } from "@/types/freelancer";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";
import type { LinkedInPainel } from "@/types/freelancer-painel";

export type CandidatoPanelTab = "resumo" | "vaga" | "linkedin" | "mensagens" | "historico";

const ABA: Record<CandidatoPanelTab, { label: string; icon: React.ElementType }> = {
  resumo: { label: "Resumo", icon: LayoutList },
  vaga: { label: "Na vaga", icon: Briefcase },
  linkedin: { label: "LinkedIn", icon: Linkedin },
  mensagens: { label: "Mensagens", icon: MessageCircle },
  historico: { label: "Histórico", icon: History },
};

enum TipoDoBadge {
  Contagem = "contagem",
  Indisponivel = "indisponivel",
}

interface BadgeDaAba {
  tipo: TipoDoBadge;
  valor?: number;
  titulo: string;
}

const BASICO_COLUMNS =
  "id, nome, email, telefone, phone_number, cidade, city, state, country, areas, pretensao_salarial, quality_seal, is_active, last_submitted_at, linkedin_url, portfolio_url, media_avaliacao_perfil, media_avaliacao_projeto, total_avaliacoes_perfil, total_avaliacoes_projeto, tags, open_to_work, open_to_work_observado_em";

type FreelancerBasico = Pick<
  FreelancerListItem,
  | "id"
  | "nome"
  | "email"
  | "telefone"
  | "cidade"
  | "city"
  | "state"
  | "country"
  | "areas"
  | "pretensao_salarial"
  | "quality_seal"
  | "is_active"
  | "last_submitted_at"
  | "linkedin_url"
  | "portfolio_url"
  | "media_avaliacao_perfil"
  | "media_avaliacao_projeto"
  | "total_avaliacoes_perfil"
  | "total_avaliacoes_projeto"
  | "open_to_work"
  | "open_to_work_observado_em"
> & { phone_number?: string | null; tags?: string[] | null; auto_tags?: string[] | null };

interface CandidatoPanelProps {
  freelancerId: string | null;
  tab: CandidatoPanelTab | null;
  onTabChange: (tab: CandidatoPanelTab) => void;
  onClose: () => void;
  /** Linha da lista, quando aberto a partir dela: o cabeçalho aparece sem esperar a busca. */
  listItem?: FreelancerListItem | null;
  /** Aberto a partir de uma vaga: mostra a aba "Na vaga". */
  vagaId?: string | null;
  /** Faixa de match da vaga (modo vaga da lista). */
  vagaSlot?: React.ReactNode;
  /** Aberto da revisão de uma leitura: a aba LinkedIn mostra esta leitura em vez de procurar a pendente. */
  linkedinJobId?: string | null;
}

function useFreelancerBasico(freelancerId: string | null, listItem?: FreelancerListItem | null) {
  const query = useQuery({
    queryKey: ["freelancer-painel-basico", freelancerId],
    enabled: !!freelancerId && !listItem,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FreelancerBasico | null> => {
      const { data, error } = await supabase
        .from("freelancers")
        .select(BASICO_COLUMNS)
        .eq("id", freelancerId as string)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as FreelancerBasico | null;
    },
  });
  return { freelancer: (listItem as FreelancerBasico | null | undefined) ?? query.data ?? null, isLoading: !listItem && query.isLoading };
}

function Section({ title, icon: Icon, children }: { title: string; icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

function WhatsAppSection({ freelancerId }: { freelancerId: string }) {
  // 403 de quem não tem a tela do WhatsApp: a seção só mostra que não há resumo.
  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp-resumo", freelancerId],
    queryFn: () => whatsappApi.resumoDoFreelancer(freelancerId),
    retry: false,
  });
  const conversa = data?.conversas?.[0];
  return (
    <Section title="WhatsApp" icon={MessageCircle}>
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !conversa ? (
        <p className="text-sm text-muted-foreground">Sem conversa registrada.</p>
      ) : (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          {conversa.ultimo_status && (
            <p className="text-sm">
              <span className="font-medium">Status atual da conversa:</span> {conversa.ultimo_status}
            </p>
          )}
          <WhatsAppLinhaDoTempo historico={conversa.historico?.slice(-3) ?? null} resumo={conversa.resumo} />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {conversa.aguardando && <WhatsAppAguardandoBadge aguardando={conversa.aguardando} desde={conversa.mensagens_ate} />}
            <span>
              Última mensagem dele:{" "}
              {conversa.ultima_mensagem_contato_em ? <RelativeTime value={conversa.ultima_mensagem_contato_em} /> : "nenhuma"}
            </span>
          </div>
        </div>
      )}
    </Section>
  );
}

function PerfilProfissionalSection({ freelancer, linkedin, isLoading }: { freelancer: FreelancerBasico; linkedin: LinkedInPainel | undefined; isLoading: boolean }) {
  return (
    <Section title="Perfil profissional" icon={Linkedin}>
      <LinkedInResumo linkedin={linkedin} isLoading={isLoading} />
      {(freelancer.areas?.length || freelancer.tags?.length || freelancer.auto_tags?.length) && (
        <div className="space-y-1.5 pt-1">
          <TagList items={freelancer.areas} max={6} />
          <div className="flex flex-wrap gap-1">
            <TagList items={freelancer.tags} variant="manual" max={5} size="xs" />
            <TagList items={freelancer.auto_tags} variant="auto" max={5} size="xs" />
          </div>
        </div>
      )}
    </Section>
  );
}

function ComACoriscoSection({ freelancer, freelancerId }: { freelancer: FreelancerBasico; freelancerId: string }) {
  const painel = useFreelancerPainel(freelancerId);
  const projetos = painel.data?.projetos_recentes ?? [];
  return (
    <Section title="Com a Corisco" icon={Briefcase}>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-muted-foreground">Nota de perfil ({freelancer.total_avaliacoes_perfil ?? 0})</div>
          <Rating value={freelancer.media_avaliacao_perfil ?? null} display="number" />
        </div>
        <div>
          <div className="text-muted-foreground">Nota de projeto ({freelancer.total_avaliacoes_projeto ?? 0})</div>
          <Rating value={freelancer.media_avaliacao_projeto ?? null} display="number" />
        </div>
      </div>
      {painel.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : projetos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum projeto com a Corisco.</p>
      ) : (
        <ul className="space-y-1.5">
          {projetos.map((projeto) => (
            <li key={projeto.id} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate">
                <span className="font-medium">{projeto.agencia ?? "Agência não informada"}</span>
                {(projeto.titulo ?? projeto.area) && <span className="text-muted-foreground"> · {projeto.titulo ?? projeto.area}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                <DateRange start={projeto.data_inicio} end={projeto.data_fim} />
                {projeto.nota != null && <Rating value={projeto.nota} display="number" />}
              </span>
            </li>
          ))}
        </ul>
      )}
      {(painel.data?.agencias.length ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground">Já trabalhou com: {painel.data?.agencias.join(", ")}</p>
      )}
    </Section>
  );
}

interface ResumoTabProps {
  freelancerId: string;
  freelancer: FreelancerBasico;
  disponibilidade: DisponibilidadeSummary | null;
}

/**
 * Na ordem da decisão: está livre e quanto cobra, o que já fez (LinkedIn),
 * a história com a Corisco e, por fim, a conversa.
 */
function ResumoTab({ freelancerId, freelancer, disponibilidade }: ResumoTabProps) {
  const painel = useFreelancerPainel(freelancerId);
  return (
    <div className="space-y-5">
      <div className="space-y-3 border-b pb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <DisponibilidadeBadge summary={disponibilidade} level="inline" />
          <span className="text-muted-foreground">
            Pretensão: <Pretensao value={freelancer.pretensao_salarial} className="font-medium text-foreground" />
          </span>
        </div>
        <ContatoLinha freelancer={freelancer} freelancerId={freelancerId} />
      </div>
      <PerfilProfissionalSection freelancer={freelancer} linkedin={painel.data?.linkedin} isLoading={painel.isLoading} />
      <ComACoriscoSection freelancer={freelancer} freelancerId={freelancerId} />
      <WhatsAppSection freelancerId={freelancerId} />
    </div>
  );
}

function VagaTab({ vagaId, freelancer, vagaSlot }: { vagaId: string; freelancer: FreelancerBasico | null; vagaSlot?: React.ReactNode }) {
  return (
    <div className="space-y-4">
      {vagaSlot}
      {freelancer && (
        <KanbanStageSelector vagaId={vagaId} freelancerId={freelancer.id} freelancerNome={freelancer.nome} freelancerEmail={freelancer.email} />
      )}
      <Button asChild variant="link" className="h-auto px-0 text-xs">
        <Link to={`/vagas/${vagaId}`}>Abrir a vaga</Link>
      </Button>
    </div>
  );
}

function HistoricoTab({ freelancerId }: { freelancerId: string }) {
  const { data, isLoading } = useFreelancerTimelineCompleta(freelancerId, "12m");
  const eventos = [...(data?.eventos ?? [])]
    .filter((evento) => evento.is_active)
    .sort((left, right) => right.occurred_at.localeCompare(left.occurred_at))
    .slice(0, 15);
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  return (
    <div className="space-y-3">
      {eventos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nada registrado nos últimos 12 meses.</p>
      ) : (
        <ol className="space-y-2">
          {eventos.map((evento) => (
            <li key={evento.id} className="border-l-2 pl-3 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{evento.label}</span>
                <RelativeTime value={evento.occurred_at} className="shrink-0 text-xs text-muted-foreground" />
              </div>
              {evento.description && <p className="text-xs text-muted-foreground">{evento.description}</p>}
              {evento.actor_nome && <p className="text-xs text-muted-foreground">por {evento.actor_nome}</p>}
            </li>
          ))}
        </ol>
      )}
      <Button asChild variant="link" className="h-auto px-0 text-xs">
        <Link to={`/freelancers/${freelancerId}?aba=linha-do-tempo`}>Ver linha do tempo completa</Link>
      </Button>
    </div>
  );
}

/** WhatsApp, LinkedIn e portfolio ao lado do nome, como no card da lista. */
function ContatoIcones({ freelancer, freelancerId }: { freelancer: FreelancerBasico; freelancerId: string }) {
  const resumoRapido = useFreelancerResumoRapido([freelancerId]).data?.get(freelancerId);
  return (
    <div className="flex shrink-0 items-center gap-0.5" data-testid="painel-contato-icones">
      <WhatsAppIcone resumo={resumoRapido?.whatsapp} telefone={freelancer.telefone || freelancer.phone_number} className="h-8 w-8" />
      <LinkedInIcone freelancerId={freelancerId} url={freelancer.linkedin_url} resumo={resumoRapido?.linkedin} />
      <ExternalLinkButton kind="portfolio" url={freelancer.portfolio_url} hideWhenEmpty={false} />
    </div>
  );
}

function ContatoLinha({ freelancer }: { freelancer: FreelancerBasico; freelancerId: string }) {
  const telefone = freelancer.telefone || freelancer.phone_number || null;
  return (
    <div className="space-y-2">
      <ContactEmail email={freelancer.email} />
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        {telefone ? (
          <>
            <span className="tabular-nums">{formatPhoneBR(telefone)}</span>
            <CopyButton value={telefone} label="Copiar telefone" />
          </>
        ) : (
          <EmptyValue label="Sem telefone" />
        )}
      </div>
    </div>
  );
}

/**
 * O que cada aba tem antes de abri-la: uma contagem, ou "indisponível" quando
 * não há o que mostrar. Usa as mesmas consultas das abas, então abrir uma
 * delas depois não busca de novo.
 */
/** A leitura do LinkedIn esperando revisão: a que veio da fila de Revisões ou, aberto de
 * outra tela, a mais recente pronta para revisão desse freelancer. */
function useJobEmRevisao(freelancerId: string | null, linkedinJobId?: string | null): string | null {
  const pendente = useQuery({
    queryKey: ["linkedin-jobs", "em-revisao", freelancerId],
    queryFn: () => linkedInApi.listJobs({ freelancer_id: freelancerId as string, status: "review_ready", limit: 1 }),
    enabled: !!freelancerId && !linkedinJobId,
    staleTime: 60 * 1000,
  });
  return linkedinJobId ?? pendente.data?.jobs[0]?.id ?? null;
}

function useBadgesDasAbas(freelancerId: string | null): Partial<Record<CandidatoPanelTab, BadgeDaAba>> {
  const id = freelancerId ?? "";
  const resumoRapido = useFreelancerResumoRapido([freelancerId]);
  const whatsapp = useQuery({
    queryKey: ["whatsapp-resumo", id],
    queryFn: () => whatsappApi.resumoDoFreelancer(id),
    enabled: !!freelancerId,
    retry: false,
  });
  const timeline = useFreelancerTimelineCompleta(freelancerId, "12m");
  const badges: Partial<Record<CandidatoPanelTab, BadgeDaAba>> = {};

  if (resumoRapido.data) {
    const linkedin = resumoRapido.data.get(id)?.linkedin;
    if (!linkedin?.headline && !linkedin?.cargo_atual) badges.linkedin = { tipo: TipoDoBadge.Indisponivel, titulo: "A plataforma ainda não leu o LinkedIn" };
  }

  if (whatsapp.isError) {
    badges.mensagens = { tipo: TipoDoBadge.Indisponivel, titulo: "Sem acesso às conversas do WhatsApp" };
  } else if (whatsapp.data) {
    const conversas = whatsapp.data.conversas?.length ?? 0;
    badges.mensagens =
      conversas > 0
        ? { tipo: TipoDoBadge.Contagem, valor: conversas, titulo: conversas === 1 ? "1 conversa" : `${conversas} conversas` }
        : { tipo: TipoDoBadge.Indisponivel, titulo: "Nenhuma conversa no WhatsApp" };
  }

  const eventos = (timeline.data?.eventos ?? []).filter((evento) => evento.is_active).length;
  if (eventos > 0) badges.historico = { tipo: TipoDoBadge.Contagem, valor: eventos, titulo: `${eventos} eventos nos últimos 12 meses` };
  return badges;
}

function BadgeDaAbaView({ badge }: { badge: BadgeDaAba | undefined }) {
  if (!badge) return null;
  return badge.tipo === TipoDoBadge.Contagem ? (
    <Badge tone="neutral" className="h-4 min-w-4 justify-center rounded-full px-1 py-0 text-[10px] tabular-nums" title={badge.titulo}>
      {badge.valor}
    </Badge>
  ) : (
    <span className="rounded-full border border-dashed px-1 text-[10px] font-normal text-muted-foreground" title={badge.titulo}>
      indisponível
    </span>
  );
}

/**
 * Painel do candidato: o que decide "chamo, adiciono, movo de etapa" sem sair
 * da lista. Cadastro completo, documentos e edição ficam na ficha.
 */
export function CandidatoPanel({ freelancerId, tab, onTabChange, onClose, listItem, vagaId, vagaSlot, linkedinJobId }: CandidatoPanelProps) {
  const openedFromVaga = Boolean(vagaId);
  useEffect(() => {
    if (freelancerId) {
      trackEvent("painel.abrir", { entidade: { tipo: "freelancer", id: freelancerId }, contexto: { origem: openedFromVaga ? "vaga" : undefined } });
    }
  }, [freelancerId, openedFromVaga]);
  const { freelancer, isLoading } = useFreelancerBasico(freelancerId, listItem);
  const { data: disponibilidadeMap } = useDisponibilidadeBatch(freelancerId ? [freelancerId] : []);
  const tabs: CandidatoPanelTab[] = vagaId ? ["resumo", "vaga", "linkedin", "mensagens", "historico"] : ["resumo", "linkedin", "mensagens", "historico"];
  const currentTab: CandidatoPanelTab = tab && tabs.includes(tab) ? tab : "resumo";
  const badges = useBadgesDasAbas(freelancerId);
  const jobEmRevisao = useJobEmRevisao(freelancerId, linkedinJobId);
  // Cinco abas não cabem com nome e badge na largura do painel: as inativas ficam só com o ícone.
  const abasCompactas = tabs.length > 4;
  const localizacao = freelancer ? formatLocation(freelancer as FreelancerListItem) : null;

  return (
    <SidePanel
      open={!!freelancerId}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      leading={
        <OpenToWorkRing openToWork={freelancer?.open_to_work} observedAt={freelancer?.open_to_work_observado_em}>
          <PersonAvatar name={freelancer?.nome ?? ""} size="md" />
        </OpenToWorkRing>
      }
      headerActions={freelancer && freelancerId && <ContatoIcones freelancer={freelancer} freelancerId={freelancerId} />}
      title={
        <span className="inline-flex items-center gap-1.5" data-testid="painel-candidato-nome">
          {freelancer?.nome ?? (isLoading ? "Carregando..." : "Freelancer")}
          {freelancer?.quality_seal && <BadgeCheck className={cn("h-4 w-4", toneTextClasses.info)} aria-label="Freelancer verificado" />}
          {freelancer?.is_active === false && <Badge tone="danger">inativo</Badge>}
          {freelancer && !freelancer.last_submitted_at && <Badge tone="neutral">sem cadastro</Badge>}
        </span>
      }
      subtitle={localizacao || undefined}
      tabs={
        <div className="-mx-1 flex gap-1 overflow-x-auto px-1" role="tablist">
          {tabs.map((tabKey) => {
            const Icon = ABA[tabKey].icon;
            return (
              <button
                key={tabKey}
                type="button"
                role="tab"
                aria-selected={currentTab === tabKey}
                onClick={() => {
                  if (tabKey !== currentTab) trackEvent("painel.aba", { entidade: { tipo: "freelancer", id: freelancerId }, contexto: { aba: tabKey } });
                  onTabChange(tabKey);
                }}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 border-b-2 px-2 py-2 text-sm font-semibold",
                  currentTab === tabKey ? "border-primary text-primary" : "border-transparent text-muted-foreground",
                )}
                data-testid={`painel-aba-${tabKey}`}
                title={ABA[tabKey].label}
                aria-label={ABA[tabKey].label}
              >
                <Icon className="h-3.5 w-3.5" />
                {(!abasCompactas || currentTab === tabKey) && ABA[tabKey].label}
                <BadgeDaAbaView badge={badges[tabKey]} />
              </button>
            );
          })}
        </div>
      }
      footer={
        freelancerId && (
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={`/freelancers/${freelancerId}/editar`}>
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Link>
            </Button>
            <Button asChild size="sm">
              <Link to={`/freelancers/${freelancerId}`}>
                <ExternalLink className="h-3.5 w-3.5" />
                Ver ficha completa
              </Link>
            </Button>
          </>
        )
      }
    >
      {!freelancerId ? null : !freelancer ? (
        isLoading ? <Skeleton className="h-40 w-full" /> : <p className="text-sm text-muted-foreground">Freelancer não encontrado.</p>
      ) : (
        <div className="space-y-5">
          {currentTab === "vaga" && vagaId ? (
            <VagaTab vagaId={vagaId} freelancer={freelancer} vagaSlot={vagaSlot} />
          ) : currentTab === "linkedin" ? (
            <CandidatoLinkedInTab freelancerId={freelancerId} linkedinUrl={freelancer.linkedin_url} linkedinJobId={jobEmRevisao} />
          ) : currentTab === "mensagens" ? (
            <CandidatoMensagensTab freelancerId={freelancerId} />
          ) : currentTab === "historico" ? (
            <HistoricoTab freelancerId={freelancerId} />
          ) : (
            <ResumoTab
              freelancerId={freelancerId}
              freelancer={freelancer}
              disponibilidade={disponibilidadeMap?.get(freelancerId) ?? null}
            />
          )}
        </div>
      )}
    </SidePanel>
  );
}
